import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Collapse,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { BuildingData, OwnerData, ExtractedContract } from "../types/contract";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { normalizeName } from "../utils/normalization";

interface BuildingDuplicateWarningProps {
  existingBuilding: BuildingData;
  existingOwner?: OwnerData;
  buildingMatchType?: "name" | "address" | "both";
  ownerMatchType: "exact" | "suggest" | "none";
  matchReason?: string;
  buildingResolution: "keep" | "update" | null;
  ownerResolution: "keep" | "update" | null;
  onBuildingResolutionChange: (resolution: "keep" | "update") => void;
  onOwnerResolutionChange: (resolution: "keep" | "update") => void;
  currentContract: ExtractedContract;
  dataSource?: "confirmed" | "draft"; // データのソース
}

const BuildingDuplicateWarning: React.FC<BuildingDuplicateWarningProps> = ({
  existingBuilding,
  existingOwner,
  buildingMatchType,
  ownerMatchType,
  matchReason,
  buildingResolution,
  ownerResolution,
  onBuildingResolutionChange,
  onOwnerResolutionChange,
  currentContract,
  dataSource = "confirmed",
}) => {
  const [expanded, setExpanded] = useState(false);

  // 予測ロジック: 家主と建物が更新されるか新規登録されるかを判定
  const predictUpdateStatus = (): {
    owner: string;
    building: string;
    ownerMatches: boolean;
    buildingMatches: boolean;
  } => {
    // 家主の判定
    let ownerPrediction: string;
    let ownerMatches = false;
    if (existingOwner) {
      const normalizedCurrentOwnerName = normalizeName(
        typeof currentContract.owner_name === "string"
          ? currentContract.owner_name
          : currentContract.owner_name?.value,
      );
      const normalizedExistingOwnerName = existingOwner.name; // DB内の名前は正規化済み

      ownerMatches = normalizedCurrentOwnerName === normalizedExistingOwnerName;
      ownerPrediction = ownerMatches
        ? "既存の情報が更新されます"
        : "このデータを新規登録します";
    } else {
      ownerPrediction = "このデータを新規登録します";
    }

    // 建物の判定
    const normalizedCurrentBuildingName = normalizeName(
      currentContract.property_name,
    );
    const normalizedExistingBuildingName = existingBuilding?.name ?? "";
    const buildingMatches =
      normalizedCurrentBuildingName === normalizedExistingBuildingName;

    // 建物は「建物名 + 家主」で識別されるため、家主名が一致しないと必ず新規登録
    let buildingPrediction: string;
    if (!ownerMatches && existingOwner) {
      // 家主名が異なる場合は、建物名が一致していても新規登録
      buildingPrediction = "このデータを新規登録します";
    } else {
      buildingPrediction = buildingMatches
        ? "既存の情報が更新されます"
        : "このデータを新規登録します";
    }

    return {
      owner: ownerPrediction,
      building: buildingPrediction,
      ownerMatches,
      buildingMatches,
    };
  };

  const updatePrediction = predictUpdateStatus();

  // 文字列の正規化と比較
  const normalizeForComparison = (value: string | null | undefined): string => {
    if (!value) return "";
    return value.trim();
  };

  const normalizePhone = (phone: string | null | undefined): string => {
    if (!phone) return "";
    return phone.replace(/[-\s()（）]/g, "");
  };

  const normalizeKana = (kana: string | null | undefined): string => {
    if (!kana) return "";
    return kana.replace(/\s+/g, "");
  };

  // フィールドが一致しているか判定
  const isFieldMatch = (
    existingValue: string | null | undefined,
    extractedValue: string | null | undefined,
    normalizer: (
      v: string | null | undefined,
    ) => string = normalizeForComparison,
  ): boolean => {
    const normalized1 = normalizer(existingValue);
    const normalized2 = normalizer(extractedValue);

    // 両方空なら一致とみなす
    if (normalized1 === "" && normalized2 === "") return true;

    return normalized1 === normalized2;
  };

  // 不一致フィールドのスタイル
  const getMismatchStyle = (isMatch: boolean) => {
    if (isMatch) return {};

    return {
      backgroundColor: "rgba(255, 243, 224, 0.5)", // 薄い黄色
      borderLeft: "3px solid #ff9800", // オレンジのアクセント
    };
  };

  // 警告が必要なケース
  const isBuildingNameMismatch = buildingMatchType === "address"; // 住所のみ一致
  const isBuildingMatchButOwnerMismatch =
    (buildingMatchType === "both" || buildingMatchType === "name") &&
    ownerMatchType === "none"; // 建物一致だが家主完全不一致（重要な警告）

  const hasWarning =
    isBuildingNameMismatch ||
    ownerMatchType === "suggest" ||
    isBuildingMatchButOwnerMismatch;

  return (
    <Alert severity={hasWarning ? "warning" : "info"} sx={{ mt: 2 }}>
      <AlertTitle>
        {existingBuilding && buildingMatchType === "both" &&
          "✓ 既存建物が見つかりました（建物名・住所が一致）"}
        {existingBuilding && buildingMatchType === "name" &&
          "既存建物が見つかりました（建物名が一致）"}
        {existingBuilding && buildingMatchType === "address" &&
          "⚠️ 既存建物が見つかりました（住所のみ一致）"}

      </AlertTitle>

      {/* セッション内データの場合のメッセージ */}
      {dataSource === "draft" && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            bgcolor: "rgba(33, 150, 243, 0.08)",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="info.dark">
            この建物は今回のアップロードで先ほど入力済みです。
            「既存データを使用する」をクリックすると、先ほど入力した内容を適用できます。
          </Typography>
        </Box>
      )}

      {/* 建物一致タイプによる表示 */}
      {existingBuilding && buildingMatchType === "both" && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            既存建物: <strong>{existingBuilding.name}</strong>
          </Typography>
          <Box
            sx={{
              mt: 1,
              p: 1,
              bgcolor: "rgba(76, 175, 80, 0.08)",
              borderRadius: 1,
              border: 1,
              borderColor: "rgba(76, 175, 80, 0.3)",
            }}
          >
            <Typography variant="body2" color="success.dark">
              ✓ 建物名が一致: <strong>{existingBuilding.name}</strong>
            </Typography>
            <Typography variant="body2" color="success.dark">
              ✓ 住所が一致: <strong>{existingBuilding.address1}</strong>
            </Typography>
          </Box>
        </Box>
      )}

      {existingBuilding && buildingMatchType === "name" && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            既存建物: <strong>{existingBuilding.name}</strong>
          </Typography>
          <Box
            sx={{
              mt: 1,
              p: 1,
              bgcolor: "rgba(33, 150, 243, 0.08)",
              borderRadius: 1,
              border: 1,
              borderColor: "rgba(33, 150, 243, 0.3)",
            }}
          >
            <Typography variant="body2" color="info.dark">
              ✓ 建物名が一致: <strong>{existingBuilding.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              住所: {existingBuilding.address1}
            </Typography>
          </Box>
        </Box>
      )}

      {existingBuilding && buildingMatchType === "address" && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            この住所の既存建物: <strong>{existingBuilding.name}</strong>
          </Typography>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: "rgba(255, 152, 0, 0.08)",
              borderRadius: 1,
              border: 1,
              borderColor: "rgba(255, 152, 0, 0.4)",
            }}
          >
            <Typography
              variant="body2"
              color="warning.dark"
              fontWeight="bold"
              gutterBottom
            >
              ⚠️ 注意: 建物名が異なります
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              ✓ 住所が一致: <strong>{existingBuilding.address1}</strong>
            </Typography>
            <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5 }}>
              ✗ 建物名が異なります
            </Typography>
          </Box>
        </Box>
      )}

      {existingOwner && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2" gutterBottom>
            家主情報の照合結果
          </Typography>

          {/* 家主完全一致 */}
          {ownerMatchType === "exact" && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                bgcolor: "rgba(76, 175, 80, 0.08)",
                borderRadius: 1,
                border: 1,
                borderColor: "rgba(76, 175, 80, 0.3)",
              }}
            >
              <Typography
                variant="body2"
                color="success.dark"
                fontWeight="bold"
              >
                ✓ 家主名が一致しています
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                家主名: <strong>{existingOwner.name}</strong>
              </Typography>
            </Box>
          )}

          {/* 家主部分一致（サジェスト） */}
          {ownerMatchType === "suggest" && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: "rgba(255, 152, 0, 0.08)",
                borderRadius: 1,
                border: 1,
                borderColor: "rgba(255, 152, 0, 0.4)",
              }}
            >
              <Typography
                variant="body2"
                color="warning.dark"
                fontWeight="bold"
                gutterBottom
              >
                ⚠️ 注意: 家主名が異なります
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                ✓ {matchReason}が一致
              </Typography>
              <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5 }}>
                ✗ 家主名が異なります
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                既存の家主名: <strong>{existingOwner.name}</strong>
              </Typography>
            </Box>
          )}

          {/* 家主不一致 */}
          {ownerMatchType === "none" && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: isBuildingMatchButOwnerMismatch
                  ? "rgba(255, 152, 0, 0.08)"
                  : "rgba(158, 158, 158, 0.08)",
                borderRadius: 1,
                border: 1,
                borderColor: isBuildingMatchButOwnerMismatch
                  ? "rgba(255, 152, 0, 0.4)"
                  : "rgba(158, 158, 158, 0.3)",
              }}
            >
              <Typography
                variant="body2"
                color={
                  isBuildingMatchButOwnerMismatch
                    ? "warning.dark"
                    : "text.primary"
                }
                fontWeight="bold"
                gutterBottom
              >
                {isBuildingMatchButOwnerMismatch && "⚠️ "}家主情報が一致しません
              </Typography>
              {isBuildingMatchButOwnerMismatch && (
                <Typography
                  variant="body2"
                  color="warning.dark"
                  sx={{ mt: 0.5, mb: 1 }}
                >
                  建物情報は一致していますが、家主が異なります。
                </Typography>
              )}
              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                既存の家主名: <strong>{existingOwner.name}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ラジオボタンによる選択UI */}
      <Box
        sx={{ mt: 2, p: 2, bgcolor: "rgba(0, 0, 0, 0.02)", borderRadius: 1 }}
      >
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          登録済みデータと一致する可能性がある【{existingBuilding ? "建物・" : ""}家主情報】の扱いを選択してください
        </Typography>

        {/* 建物情報の選択 */}
        {existingBuilding && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "white",
            borderRadius: 1,
            border: 1,
            borderColor: "rgba(0, 0, 0, 0.12)",
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            fontWeight="bold"
            color="primary"
          >
            建物情報の扱い
          </Typography>
          <FormControl component="fieldset" sx={{ mt: 1, width: "100%" }}>
            <RadioGroup
              value={buildingResolution || ""}
              onChange={(e) =>
                onBuildingResolutionChange(e.target.value as "keep" | "update")
              }
            >
              <FormControlLabel
                value="keep"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">既存の建物情報を採用</Typography>
                }
                sx={{ mb: 0.5 }}
              />
              <FormControlLabel
                value="update"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      抽出・編集結果を採用
                    </Typography>
                    {buildingResolution === "update" && (
                      <Typography
                        variant="caption"
                        color="info.main"
                        sx={{ ml: 0, display: "block", mt: 0.5 }}
                      >
                        → {updatePrediction.building}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
        )}

        {/* 家主情報の選択 */}
        {existingOwner && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "white",
              borderRadius: 1,
              border: 1,
              borderColor: "rgba(0, 0, 0, 0.12)",
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              fontWeight="bold"
              color="primary"
            >
              家主情報の扱い
            </Typography>
            <FormControl component="fieldset" sx={{ mt: 1, width: "100%" }}>
              <RadioGroup
                value={ownerResolution || ""}
                onChange={(e) =>
                  onOwnerResolutionChange(e.target.value as "keep" | "update")
                }
              >
                <FormControlLabel
                  value="keep"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      既存の家主情報を採用
                    </Typography>
                  }
                  sx={{ mb: 0.5 }}
                />
                <FormControlLabel
                  value="update"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        抽出・編集結果を採用
                      </Typography>
                      {ownerResolution === "update" && (
                        <Typography
                          variant="caption"
                          color="info.main"
                          sx={{ ml: 0, display: "block", mt: 0.5 }}
                        >
                          → {updatePrediction.owner}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1.5, display: "block" }}
        >
          ※
          賃料や契約者名など、その他の契約内容は現在の画面の入力内容が維持されます
        </Typography>

        {/* 未選択時の警告 */}
        {((existingBuilding && buildingResolution === null) || (existingOwner && ownerResolution === null)) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              新規登録となる項目があります。既存データを使用するか、抽出・編集結果を使用するか選択してください。
            </Typography>
          </Alert>
        )}
      </Box>

      {/* 詳細表示ボタン */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "既存データの詳細を閉じる" : "既存データの詳細"}
        </Button>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* 既存建物データ */}
            {existingBuilding && <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    建物情報
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", width: "40%" }}>
                          建物名
                        </TableCell>
                        <TableCell
                          sx={getMismatchStyle(
                            isFieldMatch(
                              existingBuilding.name,
                              currentContract.property_name,
                            ),
                          )}
                        >
                          {existingBuilding.name || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          建物名カナ
                        </TableCell>
                        <TableCell
                          sx={getMismatchStyle(
                            isFieldMatch(
                              existingBuilding.name_kana,
                              currentContract.building_name_kana?.value,
                              normalizeKana,
                            ),
                          )}
                        >
                          {existingBuilding.name_kana || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          建物コード
                        </TableCell>
                        <TableCell
                          sx={getMismatchStyle(
                            isFieldMatch(
                              existingBuilding.building_code,
                              currentContract.building_code?.value,
                            ),
                          )}
                        >
                          {existingBuilding.building_code || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          郵便番号
                        </TableCell>
                        <TableCell
                          sx={getMismatchStyle(
                            isFieldMatch(
                              existingBuilding.postal_code,
                              currentContract.property_postal_code?.value,
                            ),
                          )}
                        >
                          {existingBuilding.postal_code || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>住所1</TableCell>
                        <TableCell
                          sx={getMismatchStyle(
                            isFieldMatch(
                              existingBuilding.address1,
                              currentContract.property_address?.value,
                            ),
                          )}
                        >
                          {existingBuilding.address1 || "-"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>住所2</TableCell>
                        <TableCell
                          sx={getMismatchStyle(
                            isFieldMatch(
                              existingBuilding.address2,
                              currentContract.property_address2?.value,
                            ),
                          )}
                        >
                          {existingBuilding.address2 || "-"}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>}

            {/* 既存家主データ */}
            {existingOwner && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      gutterBottom
                    >
                      家主情報
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold", width: "40%" }}>
                            家主名
                          </TableCell>
                          <TableCell
                            sx={getMismatchStyle(
                              isFieldMatch(
                                existingOwner.name,
                                currentContract.owner_name?.value,
                              ),
                            )}
                          >
                            {existingOwner.name || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            家主名カナ
                          </TableCell>
                          <TableCell
                            sx={getMismatchStyle(
                              isFieldMatch(
                                existingOwner.name_kana,
                                currentContract.owner_name_kana?.value,
                                normalizeKana,
                              ),
                            )}
                          >
                            {existingOwner.name_kana || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            郵便番号
                          </TableCell>
                          <TableCell
                            sx={getMismatchStyle(
                              isFieldMatch(
                                existingOwner.postal_code,
                                currentContract.owner_postal_code?.value,
                              ),
                            )}
                          >
                            {existingOwner.postal_code || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            住所1
                          </TableCell>
                          <TableCell
                            sx={getMismatchStyle(
                              isFieldMatch(
                                existingOwner.address1,
                                currentContract.owner_address1?.value,
                              ),
                            )}
                          >
                            {existingOwner.address1 || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            住所2
                          </TableCell>
                          <TableCell
                            sx={getMismatchStyle(
                              isFieldMatch(
                                existingOwner.address2,
                                currentContract.owner_address2?.value,
                              ),
                            )}
                          >
                            {existingOwner.address2 || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            固定電話
                          </TableCell>
                          <TableCell
                            sx={getMismatchStyle(
                              isFieldMatch(
                                existingOwner.home_phone,
                                currentContract.owner_home_phone?.value,
                                normalizePhone,
                              ),
                            )}
                          >
                            {existingOwner.home_phone || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            携帯電話
                          </TableCell>
                          <TableCell
                            sx={getMismatchStyle(
                              isFieldMatch(
                                existingOwner.mobile_phone,
                                currentContract.owner_mobile_phone?.value,
                                normalizePhone,
                              ),
                            )}
                          >
                            {existingOwner.mobile_phone || "-"}
                          </TableCell>
                        </TableRow>
                        {(existingOwner.bank_1_branch_code ||
                          existingOwner.bank_1_account_number) && (
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行1支店コード
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_1_branch_code,
                                    currentContract.owner_bank_1_branch_code
                                      ?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_1_branch_code || "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行1口座種別
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_1_account_type,
                                    currentContract.owner_bank_1_account_type
                                      ?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_1_account_type || "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行1口座番号
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_1_account_number,
                                    currentContract.owner_bank_1_account_number
                                      ?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_1_account_number || "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行1口座名義
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_1_account_holder_name,
                                    currentContract
                                      .owner_bank_1_account_holder_name?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_1_account_holder_name ||
                                  "-"}
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                        {(existingOwner.bank_2_branch_code ||
                          existingOwner.bank_2_account_number) && (
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行2支店コード
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_2_branch_code,
                                    currentContract.owner_bank_2_branch_code
                                      ?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_2_branch_code || "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行2口座種別
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_2_account_type,
                                    currentContract.owner_bank_2_account_type
                                      ?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_2_account_type || "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行2口座番号
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_2_account_number,
                                    currentContract.owner_bank_2_account_number
                                      ?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_2_account_number || "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                銀行2口座名義
                              </TableCell>
                              <TableCell
                                sx={getMismatchStyle(
                                  isFieldMatch(
                                    existingOwner.bank_2_account_holder_name,
                                    currentContract
                                      .owner_bank_2_account_holder_name?.value,
                                  ),
                                )}
                              >
                                {existingOwner.bank_2_account_holder_name ||
                                  "-"}
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </Collapse>
    </Alert>
  );
};

export default BuildingDuplicateWarning;
